import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { Spinner } from '@fluentui/react-components';
import { isInTeams } from '../common/Teams';
import { Constants } from '../common/Constants';
import { AuthError } from './AuthError';
import { hasAuthenticationParams, isAuthenticationRedirect } from '../utils/MsalUtils';
import { loginRequest } from '../auth/msalConfig';

interface AuthGuardProps {
  children: React.ReactNode;
}

// Global flag to prevent multiple simultaneous login attempts
let globalLoginInProgress = false;

// Export for testing purposes
export const resetGlobalLoginState = () => {
  globalLoginInProgress = false;
};

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const location = useLocation();
  const inTeams = isInTeams();

  // Helper: Check if user should be auto-logged in
  const shouldAttemptAutoLogin = (): boolean => {
    return !isAuthenticated && 
           !localStorage.getItem('suppressAutoLogin') &&
           !isAuthenticationRedirect() &&
           !hasAuthenticationParams() &&
           !globalLoginInProgress &&
           location.pathname === '/';
  };

  // Helper: Attempt auto-login
  const attemptAutoLogin = async (): Promise<void> => {
    globalLoginInProgress = true;
    
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login failed:', error);
      globalLoginInProgress = false;
      setAuthError('Login failed. Please try clicking the login button manually.');
    }
  };

  // Helper: Handle authentication redirect delays
  const handleAuthRedirect = (isMounted: { current: boolean }): void => {
    if (isAuthenticationRedirect() || hasAuthenticationParams()) {
      setTimeout(() => {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }, 2000);
    }
  };

  useEffect(() => {
    const isMounted = { current: true };
    
    const checkAuthentication = async (): Promise<void> => {
      try {
        if (!isMounted.current) return;

        // In Teams or Web Component, always assume authenticated
        if (inTeams || Constants.IS_WEBCOMPONENT) {
          setIsLoading(false);
          setAuthError(null);
          return;
        }

        // Wait for MSAL to finish any in-progress interactions
        if (inProgress !== 'none') {
          return;
        }

        setIsLoading(false);
        setAuthError(null);

        // Auto-login logic - only on home page
        if (shouldAttemptAutoLogin()) {
          await attemptAutoLogin();
        }

        // Handle authentication redirects
        handleAuthRedirect(isMounted);
      } catch (error) {
        console.error("Authentication check failed:", error);
        if (isMounted.current) {
          setAuthError("Authentication check failed. Please try again.");
          setIsLoading(false);
        }
      }
    };

    checkAuthentication();

    return () => {
      isMounted.current = false;
    };
  }, [instance, isAuthenticated, inProgress, inTeams, retryCount, location.pathname]);

  // Show loading spinner while checking authentication
  if (isLoading || inProgress !== 'none') {
    return (
      <div className="overlay">
        <Spinner size="large" label="Authenticating..." />
      </div>
    );
  }

  // Show error if authentication failed
  if (authError) {
    return (
      <AuthError 
        error={authError} 
        onRetry={() => {
          setAuthError(null);
          setIsLoading(true);
          setRetryCount(prev => prev + 1);
          // Clear any stale state and retry
          globalLoginInProgress = false;
        }}
      />
    );
  }

  // In Teams or Web Component mode, always allow access
  if (inTeams || Constants.IS_WEBCOMPONENT) {
    return <>{children}</>;
  }

  // Redirect to home if not authenticated and not already on home page
  if (!isAuthenticated && location.pathname !== '/') {
    return <Navigate to="/" replace />;
  }

  // Show loading if on home page but not authenticated (login in progress)
  if (!isAuthenticated && location.pathname === '/') {
    return (
      <div className="overlay">
        <Spinner size="large" label="Signing in..." />
      </div>
    );
  }

  // Render children if authenticated
  return <>{children}</>;
};
